import { useEffect, type ChangeEvent } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";

import { FormDialog } from "ui/form-dialog";
import { LoadingButtonState } from "ui/button";
import { TextField } from "ui/text-field";
import { Switch } from "ui/switch";

import { useCloneProgramTemplates } from "components/modals/clone-program-templates-modal/hooks/use-clone-program-templates";
import { useValidateProgramTemplateName } from "containers/admin/clients/client/program-templates-page/hooks/use-validate-program-template-name";
import type {
  CloneProgramTemplatesModalProps,
  CloneProgramTemplatesFormData,
} from "components/modals/clone-program-templates-modal/clone-program-templates-modal.types";

import { TouchpointType } from "types/touchpoint";

const CloneProgramTemplatesModal = ({
  isOpen,
  onClose,
  selectedTemplates,
  onSuccess,
}: CloneProgramTemplatesModalProps) => {
  const defaultValues: CloneProgramTemplatesFormData = {
    items: selectedTemplates.map((t) => ({
      name: `Copy of ${t.name}`,
      applyClientTheme:
        t.type === TouchpointType.EMAIL && (t.latestVersion?.applyClientTheme ?? true),
    })),
  };

  const { validate: validateTemplateNameUnique } = useValidateProgramTemplateName({
    enabled: isOpen,
  });

  const methods = useForm<CloneProgramTemplatesFormData>({
    defaultValues,
    mode: "onChange",
  });

  const { control, formState, reset, getValues, trigger } = methods;
  const { isValid } = formState;

  const { fields } = useFieldArray({ control, name: "items" });

  const validateTemplateName = (value: string, index: number) => {
    const templateType = selectedTemplates[index]?.type;

    const uniqueResult = validateTemplateNameUnique({ name: value, type: templateType });
    if (uniqueResult !== true) return uniqueResult;

    const normalized = value.trim().toLowerCase();
    const items = getValues("items");
    const isDuplicateInForm = items?.some(
      (item, i) =>
        i !== index &&
        selectedTemplates[i]?.type === templateType &&
        item.name.trim().toLowerCase() === normalized,
    );
    if (isDuplicateInForm) return "Duplicate template name";

    return true;
  };

  // Reset form when modal opens/closes so the field array stays in sync with selectedTemplates
  useEffect(() => {
    reset({
      items: selectedTemplates.map((t) => ({
        name: `Copy of ${t.name}`,
        applyClientTheme:
          t.type === TouchpointType.EMAIL && (t.latestVersion?.applyClientTheme ?? true),
      })),
    });
  }, [isOpen, selectedTemplates, reset]);

  const {
    submitState,
    handleSubmit: handleFormSubmit,
    handleCancelClick,
  } = useCloneProgramTemplates({
    selectedTemplates,
    onClose,
    onSuccess,
  });

  const handleNameChange = (
    index: number,
    onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void,
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    onChange(e);
    if (fields.length > 1) {
      const templateType = selectedTemplates[index]?.type;
      const otherIndexesWithSameType = fields
        .map((_, i) => i)
        .filter((i) => i !== index && selectedTemplates[i]?.type === templateType);

      if (otherIndexesWithSameType.length > 0) {
        void trigger(otherIndexesWithSameType.map((i) => `items.${i}.name` as const));
      }
    }
  };

  if (!isOpen) return null;

  const isSingle = selectedTemplates.length === 1;

  return (
    <FormDialog
      open
      onClose={handleCancelClick}
      headline={`Clone template${isSingle ? "" : "s"}`}
      form={methods}
      onSubmit={handleFormSubmit}
      submitDisabled={!isValid}
      submitting={submitState === LoadingButtonState.Loading}
      submitButtonText={`Clone template${isSingle ? "" : "s"}`}
      submitLoadingMessage="Cloning templates...">
      {fields.map((field, index) => (
        <div key={field.id} className="flex flex-col gap-2">
          <Controller
            name={`items.${index}.name`}
            control={control}
            rules={{
              required: "Template name is required",
              validate: (value) => validateTemplateName(value, index),
            }}
            render={({ field: inputField, fieldState }) => (
              <TextField
                label={<span className="text-navy-800 font-bold">Template name</span>}
                helperText={`Based on template: ${selectedTemplates[index]?.name ?? ""}.`}
                required
                errMessage={fieldState.error?.message}
                {...inputField}
                onChange={(e) => handleNameChange(index, inputField.onChange, e)}
              />
            )}
          />
          {selectedTemplates[index]?.type === TouchpointType.EMAIL && (
            <Controller
              name={`items.${index}.applyClientTheme`}
              control={control}
              render={({ field: toggleField }) => (
                <Switch
                  toggleName="Enable client theme"
                  checked={toggleField.value}
                  onChange={toggleField.onChange}
                />
              )}
            />
          )}
        </div>
      ))}
    </FormDialog>
  );
};

export default CloneProgramTemplatesModal;
